import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { ThemeProvider } from './contexts/ThemeContext.jsx';
import { AuthProvider } from './contexts/AuthContext.jsx';
import { SystemNotificationProvider } from './contexts/SystemNotificationContext.jsx';
import { NetworkMonitorProvider } from './contexts/NetworkMonitorContext.jsx';
import NetworkConsole from './components/NetworkConsole/NetworkConsole.jsx';
import { ToastContainer } from 'react-toastify';
import { queryClient } from './lib/queryClient.js';
import AppLayout from './components/Layout/AppLayout.jsx';
import ProtectedRoute from './components/ProtectedRoute.jsx';
import { ROUTES } from './utils/constants.js';
import Dashboard from './pages/Dashboard/Dashboard.jsx';
import MapDashboard from './pages/MapDashboard/MapDashboard.jsx';
import NotFound from './pages/NotFound/NotFound.jsx';
import ChargerManagement from './pages/ChargerManagement/ChargerManagement.jsx';
import DRManagement from './pages/DRManagement/DRManagement.jsx';
import DRHistory from './pages/DRHistory/DRHistory.jsx';
import DRHistorySettlementDetail from './pages/DRHistorySettlementDetail/DRHistorySettlementDetail.jsx';
import ReportManagement from './pages/ReportManagement/ReportManagement.jsx';
import ReportPreview from './pages/ReportManagement/ReportPreview.jsx';
import Settings from './pages/Settings/Settings.jsx';
import UserManagement from './pages/UserManagement/UserManagement.jsx';
import SystemLog from './pages/SystemLog/SystemLog.jsx';
import AiModuleManagement from './pages/AiModuleManagement/AiModuleManagement.jsx';
import StationManagement from './pages/StationManagement/StationManagement.jsx';
import DRResourceDetail from './pages/DRResourceDetail/DRResourceDetail.jsx';
import PowerMeterContractDetail from './pages/PowerMeterContractDetail/PowerMeterContractDetail.jsx';
import StationDetail from './pages/StationDetail/StationDetail.jsx';
import ChargerDetail from './pages/ChargerDetail/ChargerDetail.jsx';
import SystemAlertHistory from './pages/SystemAlertHistory/SystemAlertHistory.jsx';
import SystemAlertDetail from './pages/SystemAlertHistory/SystemAlertDetail.jsx';
import CblModuleManagement from './pages/CblModuleManagement/CblModuleManagement.jsx';
import SystemRecoveryCommand from './pages/SystemRecoveryCommand/SystemRecoveryCommand.jsx';
import SystemConfigKeyManagement from './pages/SystemConfigKeyManagement/SystemConfigKeyManagement.jsx';
import SmsManagement from './pages/SmsManagement/SmsManagement.jsx';
import OpmDataLinkageStatus from './pages/OpmDataLinkageStatus/OpmDataLinkageStatus.jsx';
import './App.css';
import 'react-toastify/dist/ReactToastify.css';
import { NavermapsProvider } from 'react-naver-maps'
import Login from './pages/Login/Login.jsx';

import SystemLogin from './pages/Login/SystemLogin.jsx';
import ChangePassword from './pages/ChangePassword/ChangePassword.jsx';

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AuthProvider>
          <SystemNotificationProvider>
            <NavermapsProvider ncpKeyId={import.meta.env.VITE_NAVER_MAP_API_KEY || ''}>
              <Router>
                <NetworkMonitorProvider>
                <Routes>
                  <Route path="/" element={<AppLayout />}>
                    <Route index element={<Navigate to={ROUTES.DASHBOARD} replace />} />
                    <Route path={ROUTES.DASHBOARD} element={
                      <ProtectedRoute path={ROUTES.DASHBOARD}>
                        <Dashboard />
                      </ProtectedRoute>
                    } />
                    <Route path={ROUTES.MAP_DASHBOARD} element={
                      <ProtectedRoute path={ROUTES.MAP_DASHBOARD}>
                        <MapDashboard />
                      </ProtectedRoute>
                    } />
                    <Route path={`${ROUTES.MAP_DASHBOARD}/*`} element={
                      <ProtectedRoute path={ROUTES.MAP_DASHBOARD}>
                        <MapDashboard />
                      </ProtectedRoute>
                    } />
                    <Route path={ROUTES.CHARGER_MANAGEMENT} element={
                      <ProtectedRoute path={ROUTES.CHARGER_MANAGEMENT}>
                        <ChargerManagement />
                      </ProtectedRoute>
                    } />
                    <Route path={`${ROUTES.CHARGER_MANAGEMENT}/detail/:chargerSysId`} element={
                      <ProtectedRoute path={`${ROUTES.CHARGER_MANAGEMENT}/detail/:chargerSysId`}>
                        <ChargerDetail />
                      </ProtectedRoute>
                    } />
                    <Route path={ROUTES.STATION_MANAGEMENT} element={
                      <ProtectedRoute path={ROUTES.STATION_MANAGEMENT}>
                        <StationManagement />
                      </ProtectedRoute>
                    } />
                    <Route path={`${ROUTES.STATION_MANAGEMENT}/detail/:stationSysId`} element={
                      <ProtectedRoute path={`${ROUTES.STATION_MANAGEMENT}/detail/:stationSysId`}>
                        <StationDetail />
                      </ProtectedRoute>
                    } />
                    <Route path={ROUTES.DR_MANAGEMENT} element={
                      <ProtectedRoute path={ROUTES.DR_MANAGEMENT}>
                        <DRManagement />
                      </ProtectedRoute>
                    } />
                    <Route path={`${ROUTES.DR_MANAGEMENT}/detail/:drResourceSysId/opm-contracts`} element={
                      <ProtectedRoute path={`${ROUTES.DR_MANAGEMENT}/detail/:drResourceSysId/opm-contracts`}>
                        <PowerMeterContractDetail />
                      </ProtectedRoute>
                    } />
                    <Route path={`${ROUTES.DR_MANAGEMENT}/detail/:drResourceSysId`} element={
                      <ProtectedRoute path={`${ROUTES.DR_MANAGEMENT}/detail/:drResourceSysId`}>
                        <DRResourceDetail />
                      </ProtectedRoute>
                    } />
                    <Route path={ROUTES.DR_HISTORY} element={
                      <ProtectedRoute path={ROUTES.DR_HISTORY}>
                        <DRHistory />
                      </ProtectedRoute>
                    } />
                    <Route path={ROUTES.DR_HISTORY_DETAIL} element={
                      <ProtectedRoute path={ROUTES.DR_HISTORY}>
                        <DRHistorySettlementDetail />
                      </ProtectedRoute>
                    } />
                    <Route path={ROUTES.OPM_DATA_LINKAGE_STATUS} element={
                      <ProtectedRoute path={ROUTES.OPM_DATA_LINKAGE_STATUS}>
                        <OpmDataLinkageStatus />
                      </ProtectedRoute>
                    } />
                    <Route path={ROUTES.REPORT_MANAGEMENT} element={
                      <ProtectedRoute path={ROUTES.REPORT_MANAGEMENT}>
                        <ReportManagement />
                      </ProtectedRoute>
                    } />
                    <Route path={`${ROUTES.REPORT_MANAGEMENT}/preview/:reportRequestSysId`} element={
                      <ProtectedRoute path={`${ROUTES.REPORT_MANAGEMENT}/preview/:reportRequestSysId`}>
                        <ReportPreview />
                      </ProtectedRoute>
                    } />
                    <Route path={ROUTES.SETTINGS} element={
                      <ProtectedRoute path={ROUTES.SETTINGS}>
                        <Settings />
                      </ProtectedRoute>
                    } />
                    <Route path={ROUTES.SYSTEM_LOG} element={
                      <ProtectedRoute path={ROUTES.SYSTEM_LOG}>
                        <SystemLog />
                      </ProtectedRoute>
                    } />
                    <Route path={ROUTES.USER_MANAGEMENT} element={
                      <ProtectedRoute path={ROUTES.USER_MANAGEMENT}>
                        <UserManagement />
                      </ProtectedRoute>
                    } />
                    <Route path={ROUTES.MODULE_MANAGEMENT} element={
                      <ProtectedRoute path={ROUTES.MODULE_MANAGEMENT}>
                        <AiModuleManagement />
                      </ProtectedRoute>
                    } />
                    <Route path={ROUTES.SYSTEM_ALERT_HISTORY} element={
                      <ProtectedRoute path={ROUTES.SYSTEM_ALERT_HISTORY}>
                        <SystemAlertHistory />
                      </ProtectedRoute>
                    } />
                    <Route path={`${ROUTES.SYSTEM_ALERT_HISTORY}/detail/:messageSysId`} element={
                      <ProtectedRoute path={`${ROUTES.SYSTEM_ALERT_HISTORY}/detail/:messageSysId`}>
                        <SystemAlertDetail />
                      </ProtectedRoute>
                    } />
                    <Route path={ROUTES.CBL_MODULE_MANAGEMENT} element={
                      <ProtectedRoute path={ROUTES.CBL_MODULE_MANAGEMENT}>
                        <CblModuleManagement />
                      </ProtectedRoute>
                    } />
                    <Route path={ROUTES.SYSTEM_RECOVERY_COMMAND} element={
                      <ProtectedRoute path={ROUTES.SYSTEM_RECOVERY_COMMAND}>
                        <SystemRecoveryCommand />
                      </ProtectedRoute>
                    } />
                    <Route path={ROUTES.SYSTEM_CONFIG_KEY_MANAGEMENT} element={
                      <ProtectedRoute path={ROUTES.SYSTEM_CONFIG_KEY_MANAGEMENT}>
                        <SystemConfigKeyManagement />
                      </ProtectedRoute>
                    } />
                    <Route path={ROUTES.CHANGE_PASSWORD} element={
                      <ChangePassword />
                    } />
                    <Route path={ROUTES.SMS_MANAGEMENT} element={
                      <ProtectedRoute path={ROUTES.SMS_MANAGEMENT}>
                        <SmsManagement />
                      </ProtectedRoute>
                    } />
                    
                  </Route>
                  <Route path="/system-login" element={<SystemLogin />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/404" element={<NotFound code="404"/>} />
                  <Route path="/403" element={<NotFound code="403"/>} />
                  <Route path="*" element={<NotFound />} />
                </Routes>
                  <NetworkConsole />
                </NetworkMonitorProvider>
              </Router>
              <ToastContainer position="top-right" autoClose={5000} newestOnTop pauseOnFocusLoss={false} theme="light" />
            </NavermapsProvider>
          </SystemNotificationProvider>
      </AuthProvider>
      </ThemeProvider>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
}

export default App;
